import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Avatar,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Divider,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  Alert,
  AlertIcon,
  Button,
  useColorModeValue,
  Progress,
  Tooltip,
  Icon
} from '@chakra-ui/react';
import { FiUser, FiTrendingUp, FiCalendar, FiAward } from 'react-icons/fi';
import { useDataStore } from '../../contexts/DataStoreContext';
import { LoadingSpinner } from '../Common/LoadingSpinner';

export const FighterProfile = ({ fighterId, fighter: initialFighter }) => {
  const dataStore = useDataStore();
  const [fighter, setFighter] = useState(initialFighter || null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(!initialFighter);
  const [error, setError] = useState('');

  useEffect(() => {
    if (fighterId && !initialFighter) {
      loadFighter();
    } else if (initialFighter) {
      setFighter(initialFighter);
      loadStats(initialFighter.id);
    }
  }, [fighterId, initialFighter]);

  // Update fighter data when DataStore changes
  useEffect(() => {
    if (fighterId && !initialFighter && !dataStore.isLoading) {
      const fighterData = dataStore.getFighter(fighterId);
      if (fighterData && (!fighter || fighter.updatedAt !== fighterData.updatedAt)) {
        setFighter(fighterData);
        loadStats(fighterData.id);
      }
    }
  }, [fighterId, initialFighter, dataStore.fighters, dataStore.fights, dataStore.isLoading, fighter]);

  const loadFighter = async () => {
    try {
      setLoading(true);
      const fighterData = dataStore.getFighter(fighterId);
      if (fighterData) {
        setFighter(fighterData);
        loadStats(fighterData.id);
      } else {
        setError('Fighter not found');
      }
    } catch (err) {
      setError(err.message || 'Failed to load fighter');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = (id) => {
    try {
      if (dataStore.isLoading) {
        return;
      }
      const statsData = dataStore.getFighterStats(id);
      setStats(statsData);
    } catch (err) {
      console.error('Failed to load fighter stats:', err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming': return 'blue';
      case 'active': return 'green';
      case 'completed': return 'gray';
      default: return 'gray';
    }
  };

  const getResultBadge = (fight, fighterId) => {
    if (fight.status !== 'completed') return null;

    if (!fight.winner) {
      return <Badge colorScheme="yellow" size="sm">Draw</Badge>;
    }

    const isWin = fight.winner === fighterId;
    return (
      <Badge colorScheme={isWin ? 'green' : 'red'} size="sm">
        {isWin ? 'Win' : 'Loss'}
      </Badge>
    );
  };

  const getOpponentName = (fight, fighterId) => {
    // Use the opponent data if available (from enhanced stats)
    if (fight.opponent) {
      return fight.opponent.name;
    }

    // Fallback to original logic
    if (fight.position === 'fighter1') {
      return fight.fighter2?.name || 'Unknown Fighter';
    } else {
      return fight.fighter1?.name || 'Unknown Fighter';
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading fighter profile..." />;
  }

  if (error || !fighter) {
    return (
      <Alert status="error">
        <AlertIcon />
        {error || 'Fighter not found'}
      </Alert>
    );
  }

  return (
    <Box maxW="6xl" mx="auto" p={6}>
      {/* Fighter Header */}
      <Card mb={6}>
        <CardBody>
          <HStack spacing={6} align="start">
            <Avatar
              size="2xl"
              name={fighter.name}
              src={fighter.photoUrl}
              color="gray.700"
              bg="white"
            />

            <VStack align="start" spacing={3} flex={1}>
              <VStack align="start" spacing={1}>
                <Heading size="xl" color="white">{fighter.name}</Heading>
                {fighter.nickname && (
                  <Text fontSize="lg" color="gray.300" fontStyle="italic">
                    "{fighter.nickname}"
                  </Text>
                )}
              </VStack>

              <HStack spacing={4} flexWrap="wrap">
                {fighter.weightClass && (
                  <Badge colorScheme="blue" variant="solid">
                    {fighter.weightClass}
                  </Badge>
                )}
                {fighter.record && (
                  <Badge colorScheme="gray" variant="outline">
                    Record: {fighter.record}
                  </Badge>
                )}
                {stats?.isActive && (
                  <Badge colorScheme="green" variant="solid">
                    Active
                  </Badge>
                )}
              </HStack>

              {fighter.gym && (
                <HStack>
                  <Icon as={FiUser} color="gray.400" />
                  <Text color="gray.300">{fighter.gym}</Text>
                </HStack>
              )}
            </VStack>
          </HStack>
        </CardBody>
      </Card>

      {/* Statistics */}
      {stats && (
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6} mb={6}>
          <Card>
            <CardBody textAlign="center">
              <Stat>
                <StatLabel color="white">Total Fights</StatLabel>
                <StatNumber color="white">{stats.totalFights}</StatNumber>
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody textAlign="center">
              <Stat>
                <StatLabel color="white">Wins</StatLabel>
                <StatNumber color="green.400">{stats.wins}</StatNumber>
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody textAlign="center">
              <Stat>
                <StatLabel color="white">Losses</StatLabel>
                <StatNumber color="red.400">{stats.losses}</StatNumber>
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody textAlign="center">
              <Stat>
                <StatLabel color="white">Win Rate</StatLabel>
                <StatNumber color="white">
                  {stats.winPercentage.toFixed(1)}%
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>
      )}

      {/* Win Percentage Visual */}
      {stats && stats.totalFights > 0 && (
        <Card mb={6}>
          <CardHeader>
            <Heading size="md" color="white">Performance Overview</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4}>
              <Box w="full">
                <HStack justify="space-between" mb={2}>
                  <Text color="gray.300">Win Percentage</Text>
                  <Text color="white" fontWeight="bold">
                    {stats.winPercentage.toFixed(1)}%
                  </Text>
                </HStack>
                <Progress
                  value={stats.winPercentage}
                  colorScheme={stats.winPercentage >= 70 ? 'green' : stats.winPercentage >= 50 ? 'yellow' : 'red'}
                  size="lg"
                  borderRadius="md"
                />
              </Box>

              <SimpleGrid columns={3} spacing={4} w="full">
                <Box textAlign="center">
                  <Text fontSize="2xl" fontWeight="bold" color="green.400">
                    {stats.wins}
                  </Text>
                  <Text fontSize="sm" color="gray.300">Wins</Text>
                </Box>
                <Box textAlign="center">
                  <Text fontSize="2xl" fontWeight="bold" color="red.400">
                    {stats.losses}
                  </Text>
                  <Text fontSize="sm" color="gray.300">Losses</Text>
                </Box>
                <Box textAlign="center">
                  <Text fontSize="2xl" fontWeight="bold" color="yellow.400">
                    {stats.draws}
                  </Text>
                  <Text fontSize="sm" color="gray.300">Draws</Text>
                </Box>
              </SimpleGrid>
            </VStack>
          </CardBody>
        </Card>
      )}

      {/* Recent Fights */}
      {stats?.recentFights?.length > 0 && (
        <Card mb={6}>
          <CardHeader>
            <Heading size="md" color="white">Recent Fights</Heading>
          </CardHeader>
          <CardBody>
            <TableContainer>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th color="gray.300">Date</Th>
                    <Th color="gray.300">Opponent</Th>
                    <Th color="gray.300">Event</Th>
                    <Th color="gray.300">Status</Th>
                    <Th color="gray.300">Result</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {stats.recentFights.map((fight) => (
                    <Tr key={fight.id}>
                      <Td color="white">
                        {fight.eventDate instanceof Date
                          ? fight.eventDate.toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })
                          : fight.updatedAt instanceof Date
                            ? fight.updatedAt.toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })
                            : 'N/A'
                        }
                      </Td>
                      <Td color="white" fontWeight="medium">
                        {getOpponentName(fight, fighter.id)}
                      </Td>
                      <Td color="white">
                        <Text fontSize="sm" noOfLines={1}>
                          {fight.eventName || 'Event'}
                        </Text>
                      </Td>
                      <Td>
                        <Badge colorScheme={getStatusColor(fight.status)} size="sm">
                          {fight.status}
                        </Badge>
                      </Td>
                      <Td>
                        {getResultBadge(fight, fighter.id)}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </CardBody>
        </Card>
      )}

      {/* Fighter Details */}
      <Card>
        <CardHeader>
          <Heading size="md" color="white">Fighter Information</Heading>
        </CardHeader>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <VStack align="start" spacing={3}>
              <Box>
                <Text fontWeight="bold" color="gray.300" mb={1}>Weight Class</Text>
                <Text color="white">{fighter.weightClass || 'Not specified'}</Text>
              </Box>

              <Box>
                <Text fontWeight="bold" color="gray.300" mb={1}>Record</Text>
                <Text color="white">{fighter.record || 'Not available'}</Text>
              </Box>

              {fighter.gym && (
                <Box>
                  <Text fontWeight="bold" color="gray.300" mb={1}>Gym/Team</Text>
                  <Text color="white">{fighter.gym}</Text>
                </Box>
              )}
            </VStack>

            <VStack align="start" spacing={3}>
              {fighter.hometown && (
                <Box>
                  <Text fontWeight="bold" color="gray.300" mb={1}>Hometown</Text>
                  <Text color="white">{fighter.hometown}</Text>
                </Box>
              )}

              {fighter.age && (
                <Box>
                  <Text fontWeight="bold" color="gray.300" mb={1}>Age</Text>
                  <Text color="white">{fighter.age}</Text>
                </Box>
              )}

              <Box>
                <Text fontWeight="bold" color="gray.300" mb={1}>Status</Text>
                <Badge colorScheme={stats?.isActive ? 'green' : 'gray'}>
                  {stats?.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </Box>
            </VStack>
          </SimpleGrid>

          {fighter.bio && (
            <>
              <Divider my={6} />
              <Box>
                <Text fontWeight="bold" color="white" mb={3}>Biography</Text>
                <Text color="gray.200" lineHeight="tall">
                  {fighter.bio}
                </Text>
              </Box>
            </>
          )}
        </CardBody>
      </Card>
    </Box>
  );
};

export default FighterProfile;
